import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Bulk-migrate every (or a chosen subset of) wallet cards owned by a single
 * customer to the Stripe Visa network. Used from Admin → Users → "Migrate cards"
 * row action.
 *
 *   - Loads `/admin/users/{id}/migratable-cards` on open.
 *   - Each card row has a checkbox; "Select all" toggles every row.
 *   - "Migrate selected" POSTs to `/admin/users/{id}/migrate-cards-to-network`
 *     and renders a per-card success/failure list.
 */
export function BulkMigrateDialog({ open, onClose, user }) {
  const [loading, setLoading] = useState(false);
  const [cards, setCards] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [stopOnFirst, setStopOnFirst] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    let alive = true;
    setLoading(true);
    setCards([]);
    setSelected(new Set());
    setResults(null);
    api
      .get(`/admin/users/${user.id}/migratable-cards`)
      .then((r) => {
        if (!alive) return;
        const list = r.data?.cards || [];
        setCards(list);
        setSelected(new Set(list.map((c) => c.id))); // default: all selected
      })
      .catch(() => { if (alive) setCards([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, user]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => (prev.size === cards.length ? new Set() : new Set(cards.map((c) => c.id))));
  };

  const totalSelectedUsd = cards
    .filter((c) => selected.has(c.id))
    .reduce((s, c) => s + (c.balance_usd || 0), 0);

  const run = async () => {
    if (selected.size === 0) {
      toast.error("Select at least one card to migrate");
      return;
    }
    if (!window.confirm(
      `Migrate ${selected.size} card${selected.size === 1 ? "" : "s"} ($${totalSelectedUsd.toFixed(2)} total) to the Stripe Visa network?\n\n` +
      `• PAN, CVV, expiry, and QR will all change for each card\n` +
      `• Balances and history are preserved\n` +
      `• You'll need to re-deliver each card to its receiver (old QRs no longer work)`
    )) return;
    setBusy(true);
    setResults(null);
    try {
      const r = await api.post(`/admin/users/${user.id}/migrate-cards-to-network`, {
        target_provider: "stripe",
        card_ids: Array.from(selected),
        stop_on_first_error: stopOnFirst,
        auto_redeliver: autoRedeliver,
      });
      setResults(r.data);
      const { succeeded, failed, total } = r.data;
      if (failed === 0) {
        toast.success(`All ${succeeded} of ${total} cards migrated`);
      } else {
        toast.error(`${succeeded} succeeded, ${failed} failed (of ${total})`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk migration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent
        className="bg-[#FBF9F6] border-[#E5E0D8] max-w-2xl max-h-[85vh] overflow-y-auto"
        data-testid="bulk-migrate-dialog"
      >
        <DialogHeader>
          <DialogTitle className="serif text-2xl text-[#0A1F16]">
            Migrate cards to Stripe Visa
          </DialogTitle>
          <DialogDescription className="text-[#4A5C50]">
            {user ? (
              <>
                Re-provision {user.email}'s wallet cards on the Stripe card network.
                Balances and history stay intact; PAN, CVV, expiry, and QR change.
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <p className="text-sm text-[#4A5C50] py-6" data-testid="bulk-migrate-loading">Loading eligible cards…</p>
        )}

        {!loading && cards.length === 0 && (
          <div className="rounded-lg border border-dashed border-[#E5E0D8] bg-white p-5 text-sm text-[#4A5C50]" data-testid="bulk-migrate-empty">
            No eligible wallet cards to migrate. (We only show <strong>active</strong> cards currently on the closed-loop wallet.)
          </div>
        )}

        {!loading && cards.length > 0 && !results && (
          <>
            <div className="flex items-center justify-between mt-2 mb-2">
              <button
                type="button"
                onClick={toggleAll}
                className="text-xs font-medium text-[#184A30] hover:underline"
                data-testid="bulk-migrate-toggle-all"
              >
                {selected.size === cards.length ? "Unselect all" : "Select all"}
              </button>
              <span className="text-xs text-[#4A5C50] mono">
                {selected.size} of {cards.length} selected · ${totalSelectedUsd.toFixed(2)}
              </span>
            </div>
            <div className="rounded-lg border border-[#E5E0D8] bg-white divide-y divide-[#E5E0D8] overflow-hidden">
              {cards.map((c) => (
                <label
                  key={c.id}
                  className="flex items-center gap-3 p-3 cursor-pointer hover:bg-[#FBF9F6]"
                  data-testid={`bulk-migrate-card-${c.id}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(c.id)}
                    onChange={() => toggle(c.id)}
                    className="h-4 w-4 accent-[#184A30]"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0A1F16] truncate">{c.receiver_name || "—"}</p>
                    <p className="text-[11px] text-[#7A8B82] mono truncate">{c.receiver_email || ""}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="mono text-xs text-[#0A1F16]">•••• {c.last4 || "----"}</p>
                    <p className="text-[11px] text-[#4A5C50] mono">${(c.balance_usd || 0).toFixed(2)}</p>
                  </div>
                </label>
              ))}
            </div>
            <label className="flex items-center gap-2 mt-3 text-xs text-[#4A5C50] cursor-pointer">
              <input
                type="checkbox"
                checked={autoRedeliver}
                onChange={(e) => setAutoRedeliver(e.target.checked)}
                className="h-3.5 w-3.5 accent-[#184A30]"
                data-testid="bulk-migrate-auto-redeliver"
              />
              Auto-email + SMS the new card to each receiver after migration
            </label>
            <label className="flex items-center gap-2 mt-2 text-xs text-[#4A5C50] cursor-pointer">
              <input
                type="checkbox"
                checked={stopOnFirst}
                onChange={(e) => setStopOnFirst(e.target.checked)}
                className="h-3.5 w-3.5 accent-[#184A30]"
                data-testid="bulk-migrate-stop-on-first"
              />
              Stop on first failure (otherwise continue past errors)
            </label>
          </>
        )}

        {results && (
          <ResultsList results={results} />
        )}

        <DialogFooter className="mt-4 gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            className="rounded-xl"
            data-testid="bulk-migrate-close"
          >
            {results ? "Close" : "Cancel"}
          </Button>
          {!results && cards.length > 0 && (
            <Button
              onClick={run}
              disabled={busy || selected.size === 0}
              className="rounded-xl bg-[#CBA35C] hover:bg-[#B08D45] text-white disabled:opacity-50"
              data-testid="bulk-migrate-run"
            >
              {busy ? `Migrating ${selected.size}…` : `Migrate ${selected.size} card${selected.size === 1 ? "" : "s"} →`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ResultsList({ results }) {
  return (
    <div className="mt-3" data-testid="bulk-migrate-results">
      <div className="flex flex-wrap gap-2 mb-3 text-xs">
        <span className="px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-900 mono">
          ✓ {results.succeeded} succeeded
        </span>
        {results.failed > 0 && (
          <span className="px-2.5 py-1 rounded-full bg-rose-50 border border-rose-200 text-rose-900 mono">
            ✗ {results.failed} failed
          </span>
        )}
        <span className="px-2.5 py-1 rounded-full bg-stone-50 border border-stone-200 text-stone-700 mono">
          {results.total} total
        </span>
      </div>
      <div className="rounded-lg border border-[#E5E0D8] bg-white divide-y divide-[#E5E0D8] overflow-hidden max-h-72 overflow-y-auto">
        {results.results.map((r, i) => (
          <div
            key={r.card_id || `r-${i}`}
            className="flex items-start gap-3 p-3"
            data-testid={`bulk-migrate-result-${r.card_id || i}`}
          >
            <span
              className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${r.ok ? "bg-emerald-500" : "bg-rose-500"}`}
              aria-hidden="true"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[#0A1F16] truncate">{r.receiver_name || r.card_id}</p>
              <p className="text-[11px] text-[#7A8B82] mono truncate">{r.receiver_email || ""}</p>
              {r.ok ? (
                <p className="text-[11px] text-[#4A5C50] mt-0.5 mono">
                  •••• {r.old_last4} → •••• {r.new_last4} · exp {r.new_expiry}
                </p>
              ) : (
                <p className="text-[11px] text-rose-700 mt-0.5">{r.error}</p>
              )}
              {r.redelivery && (
                <p className="text-[11px] text-[#4A5C50] mt-0.5">
                  {r.redelivery.email && (
                    <span data-testid="redelivery-email" className={r.redelivery.email.sent ? "text-emerald-700" : "text-rose-700"}>
                      {r.redelivery.email.sent ? "✓ emailed" : "✗ email failed"}
                    </span>
                  )}
                  {r.redelivery.email && r.redelivery.sms && <span> · </span>}
                  {r.redelivery.sms && (
                    <span data-testid="redelivery-sms" className={r.redelivery.sms.sent ? "text-emerald-700" : "text-rose-700"}>
                      {r.redelivery.sms.sent ? "✓ texted" : "✗ SMS failed"}
                    </span>
                  )}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default BulkMigrateDialog;
